import React, { useEffect, useReducer, useState, useContext } from "react";

import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";

import { makeStyles } from 'tss-react/mui';

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { StandardTableContainer, useStandardTableStyles } from "../../components/StandardTable";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutlined, Edit } from "@mui/icons-material";
import QueueModal from "../../components/QueueModal";
import QueueGroupModal from "../../components/QueueGroupModal";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "../../services/toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import { socketConnection } from "../../services/socket";

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1.5),
    overflowY: "scroll",
    background: "transparent",
    ...theme.scrollbarStyles,
  },
  swatch: {
    width: 22,
    height: 22,
    borderRadius: 6,
  },
  muted: {
    color: theme.palette.text.disabled,
  },
  automationCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  empty: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    padding: theme.spacing(2),
  },
}));

const INTEGRATION_TYPE_LABELS = {
  dialogflow: "DialogFlow",
  n8n: "N8N",
  webhook: "WebHooks",
  typebot: "Typebot",
};

const automationOf = (queue) => {
  if (queue.prompt) {
    return { label: i18n.t("queues.table.automationPrompt"), name: queue.prompt.name, color: "primary" };
  }
  if (queue.queueIntegrations) {
    const type = queue.queueIntegrations.type;
    const typeLabel = INTEGRATION_TYPE_LABELS[type] || type;
    return {
      label: `${i18n.t("queues.table.automationIntegration")} · ${typeLabel}`,
      name: queue.queueIntegrations.name,
      color: "secondary",
    };
  }
  return null;
};

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const { classes } = useStyles();
  const tableClasses = useStandardTableStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [queueGroupModalOpen, setQueueGroupModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [ratingByQueueEnabled, setRatingByQueueEnabled] = useState(false);

  const loadQueues = async () => {
    try {
      const { data } = await api.get("/queue");
      dispatch({ type: "LOAD_QUEUES", payload: data });
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadQueues();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      if (Array.isArray(data)) {
        const userRating = data.find((d) => d.key === "userRating");
        if (userRating) {
          setRatingByQueueEnabled(userRating.value === "queue");
        }
      }
    });
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-queue`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
        loadQueues();
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Queue deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <MainContainer maxWidth={false}>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${
            selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
      />
      <QueueGroupModal
        open={queueGroupModalOpen}
        onClose={() => setQueueGroupModalOpen(false)}
      />
      <MainHeader>
        <Title>{i18n.t("queues.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Can
            role={user.profile}
            perform="queues-page:manageGroups"
            yes={() => (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setQueueGroupModalOpen(true)}
              >
                {i18n.t("queues.buttons.manageGroups")}
              </Button>
            )}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenQueueModal}
          >
            {i18n.t("queues.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <div className={classes.mainPaper}>
        <StandardTableContainer>
          <Table size="small" stickyHeader>
            <TableHead className={tableClasses.head}>
              <TableRow>
                <TableCell>{i18n.t("queues.table.name")}</TableCell>
                <TableCell align="center">{i18n.t("queues.table.color")}</TableCell>
                <TableCell>{i18n.t("queues.table.group")}</TableCell>
                <TableCell>{i18n.t("queues.table.automation")}</TableCell>
                <TableCell align="center">{i18n.t("queues.table.orderQueue")}</TableCell>
                <TableCell>{i18n.t("queues.table.greeting")}</TableCell>
                {ratingByQueueEnabled && (
                  <TableCell align="center">{i18n.t("queues.table.rating")}</TableCell>
                )}
                <TableCell align="center">{i18n.t("queues.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queues.map((queue) => {
                const automation = automationOf(queue);
                return (
                <TableRow key={queue.id} hover>
                  <TableCell>{queue.name}</TableCell>
                  <TableCell align="center">
                    <div className={classes.swatch} style={{ backgroundColor: queue.color, margin: "0 auto" }} />
                  </TableCell>
                  <TableCell>
                    {queue.queueGroup?.name || <span className={classes.muted}>—</span>}
                  </TableCell>
                  <TableCell>
                    {automation ? (
                      <div className={classes.automationCell}>
                        <Chip size="small" variant="outlined" color={automation.color} label={automation.label} />
                        {automation.name}
                      </div>
                    ) : (
                      <span className={classes.muted}>—</span>
                    )}
                  </TableCell>
                  <TableCell align="center">{queue.orderQueue}</TableCell>
                  <TableCell>{queue.greetingMessage}</TableCell>
                  {ratingByQueueEnabled && (
                    <TableCell align="center">
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          queue.ratingEnabled === false
                            ? i18n.t("queues.table.ratingOff")
                            : i18n.t("queues.table.ratingOn")
                        }
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEditQueue(queue)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedQueue(queue);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
              {loading && <TableRowSkeleton columns={ratingByQueueEnabled ? 8 : 7} />}
            </TableBody>
          </Table>
          {!loading && queues.length === 0 && (
            <div className={classes.empty}>Todavía no hay ninguna cola cargada.</div>
          )}
        </StandardTableContainer>
      </div>
    </MainContainer>
  );
};

export default Queues;
